import { Flex, Grid } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';
import { Heading } from 'ui-components';
import authApi from '~/authApi';
import { Creator } from '~/clientApi/creator.model';
import {
  ChannelMetrics,
  EmptyState,
  MetricCard,
  MetricsInfoPopover,
  NewMetricCard,
  PlatformMetricsSource,
  isZeroOrUndefined,
} from '~/components/CreatorChannelMetrics';
import {
  FEATURE_FLAGS,
  PARTIALLY_SUPPORTED_PLATFORMS,
  USER_ROLES,
} from '~/constants';
import { DEMOGRAPHICS_SOURCES } from '~/constants/demographicsSources';
import { SupportedPlatforms } from '~/constants/platforms';
import { useGetCreatorProfile } from '~/hooks/api/useGetCreatorProfile';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { usePlatformMetrics } from '~/hooks/usePlatformMetrics';
import { useTalentManagerAccess } from '~/hooks/useTalentManagerAccess';
import { selectTalentManager } from '~/selectors/talent-manager';
import { useCurrentUserAccess } from '~/userManagementApi/userAccess';
import { MeResponse } from '~/userManagementApi/userAccess.types';

import { AllEmptyState } from '../TalentCharts/EmptyState/AllEmptyState';
import {
  CREATOR_MESSAGES,
  MANAGER_MESSAGES,
} from '../TalentCharts/EmptyState/utils';
import { STMPredictionLabel } from '../TalentCharts/STMPredictionLabel';

interface Props {
  creatorPlatformMetrics: ChannelMetrics | undefined;
  platform: SupportedPlatforms;
  isBuilder?: boolean;
  selectedMetrics?: string[];
  platformMetricsSource: PlatformMetricsSource | undefined;
}

export const MetricsContainer: React.FC<Props> = ({
  creatorPlatformMetrics,
  platform,
  isBuilder,
  selectedMetrics,
  platformMetricsSource,
}) => {
  const isPerformancePredictionMetricsEnabled = useFeature(
    FEATURE_FLAGS.foamPerformancePredictionMetrics,
  ).isEnabled;
  const isModashAudienceDemographicsEnabled = useFeature(
    FEATURE_FLAGS.foamModashAudienceDemographics,
  ).isEnabled;
  const isTalentRecordGrowthRateEnabled = useFeature(
    FEATURE_FLAGS.foamTalentRecordGrowthRate,
  ).isEnabled;
  const isMetricsCardsEnabled = useFeature(
    FEATURE_FLAGS.foamMetricsCards,
  ).isEnabled;

  const router = useRouter();
  const slug = router.query.creatorSlug as string;
  const { data: creator } = useGetCreatorProfile({ slug });
  const manager = useSelector(selectTalentManager);

  const { isPlatformConnected, isVerifiedCreator, connectionStatus } =
    usePlatformMetrics({ creator, platform });

  const userAccessData = useCurrentUserAccess({
    entityId: manager?.agency?.id,
  });

  const { shouldHaveAccessAsManagerComanagerAdmin } = useTalentManagerAccess(
    creator as Creator,
    userAccessData.data as MeResponse,
  );

  const metricsToShow = creatorPlatformMetrics?.[platform] ?? [];

  const isEmpty = useMemo(() => {
    return match(platform)
      .with(PARTIALLY_SUPPORTED_PLATFORMS.snapchat, () => {
        // Omit profile tier and loyalty score metrics for snapchat while checking whether the metrics are empty
        const snapMetrics = metricsToShow.filter(
          ({ title }) => title !== 'Profile Tier' && title !== 'Loyalty Score', // because these values are hardcoded
        );

        return snapMetrics.every(({ value }) => isZeroOrUndefined(value));
      })
      .otherwise(() =>
        metricsToShow.every(({ value }) => isZeroOrUndefined(value)),
      );
  }, [metricsToShow, platform]);

  const demographicsSource = platformMetricsSource?.[platform];
  const isSTMPrediction =
    demographicsSource === DEMOGRAPHICS_SOURCES.modashEstimated &&
    isModashAudienceDemographicsEnabled &&
    isPerformancePredictionMetricsEnabled;

  const isPredictedVisible =
    connectionStatus === 'off-platform' &&
    isSTMPrediction &&
    isPerformancePredictionMetricsEnabled;

  /** Nothing usable is connected — asking the viewer to connect is correct. */
  const showConnectPrompt = useMemo(
    () => !isPlatformConnected && !isPredictedVisible,
    [isPlatformConnected, isPredictedVisible],
  );

  /**
   * Connected, but every metric on this platform came back zero/undefined.
   * That is a data-availability problem, not a connection problem, so it must
   * not be reported as "connect this account" (FOAM-23134).
   */
  const hasNoMetricValues = useMemo(
    () => isEmpty && !isPredictedVisible,
    [isEmpty, isPredictedVisible],
  );

  const showEmptyState = showConnectPrompt || hasNoMetricValues;

  const { roles } = authApi.user;
  const isCreator =
    roles.includes(USER_ROLES.creator) ||
    roles.includes(USER_ROLES.managed) ||
    roles.includes(USER_ROLES.echoCreator);

  if (showEmptyState) {
    const messages = isCreator ? CREATOR_MESSAGES : MANAGER_MESSAGES;
    const messageToShow = showConnectPrompt
      ? messages.notConnected.message
      : messages.metricsUnavailable.message;

    if (isTalentRecordGrowthRateEnabled) {
      return (
        <AllEmptyState
          text={messageToShow}
          creator={creator as Creator}
          buttonType={showConnectPrompt ? 'connect' : undefined}
        />
      );
    }

    return (
      <EmptyState
        isManager={shouldHaveAccessAsManagerComanagerAdmin}
        platform={platform}
        hasError={isBuilder || isVerifiedCreator}
        isBuilder={isBuilder}
        isSTMPredicted={isPerformancePredictionMetricsEnabled}
        messageToShow={messageToShow}
      />
    );
  }

  // Temporary fix: Tittle mapping for deprecated metrics in Meta Graph API v22
  // TODO: Remove this (https://whalar.atlassian.net/browse/FOAM-13539) once BE updates the metric names (https://whalar.atlassian.net/browse/FOAM-13538)
  const titleMapping = {
    'Average Impressions': 'Average Views',
    'Average Reels Impressions': 'Average Reels Views',
    'Average Story Impressions': 'Average Story Views',
  };
  const filteredMetricsToShow = metricsToShow.filter((metric) => {
    if (isZeroOrUndefined(metric.value)) return false;
    // If isBuilder is false, render all metrics. Otherwise, check if the metric.title exists in selectedMetrics for the platform.
    if (!isBuilder) return true;

    const normalizedMetricTitle =
      titleMapping[metric.title as keyof typeof titleMapping] || metric.title;

    return selectedMetrics?.some((selected) => {
      const normalizedSelected =
        titleMapping[selected as keyof typeof titleMapping] || selected;
      return normalizedSelected === normalizedMetricTitle;
    });
  });

  return (
    <>
      {!isBuilder ? (
        <Flex justifyContent="space-between">
          <Heading size="sm">Talent & Channel metrics</Heading>
          <MetricsInfoPopover platform={platform} />
        </Flex>
      ) : null}

      <Grid
        templateColumns={`repeat(auto-fit, minmax(300px, ${
          filteredMetricsToShow.length < 3 ? '300px' : '1fr'
        }))`}
        gap={4}
      >
        {filteredMetricsToShow.map((metric) =>
          isMetricsCardsEnabled ? (
            <NewMetricCard
              key={metric.title}
              metricTitle={metric.title}
              metricSubtitle={metric.subtitle}
              metricValue={metric.value}
              metricTransformValue={metric.transformValue}
              isDisabled={metric.isDisabled}
            />
          ) : (
            <MetricCard
              key={metric.title}
              metricTitle={metric.title}
              metricValue={metric.value}
              metricTransformValue={metric.transformValue}
              isDisabled={metric.isDisabled}
            />
          ),
        )}
      </Grid>

      <STMPredictionLabel isSTMEnabled={isSTMPrediction} platform={platform} />
    </>
  );
};
